/**
 * Which chat an action belongs to, so an outcome that arrives on its own is recorded there and never in whichever chat
 * happens to be open (owner report 2026-10-09: an event created from one chat finished while he was drafting his PPP in
 * a new chat, and "Done - Create the event ..." appeared at the bottom of the new chat). Two things report outcomes
 * without knowing which chat is open: the requester inbox, which re-reports every finished action on each poll for as
 * long as gyro-svc lists it (an hour), and the Office pane's runner, which settles an action whenever its
 * acknowledgement lands.
 *
 * An action belongs to the chat it was confirmed (or cancelled) in, else the one its card was first shown in. Session
 * memory only, like LocalFileContinuations: a saved transcript keeps role and content alone, so after a reload nothing
 * here knows an action, and its outcome adds nothing to any chat (it never lands in the open one by default).
 */
/** How many actions are remembered (the oldest is forgotten first). */
export const ACTION_CONVERSATIONS_MAX = 256;
export class ActionConversations {
    entries = new Map();
    /** The action's card was shown in `conversation`: it belongs there, unless it already belongs to a chat. */
    shown(actionId, owner, conversation) {
        if (this.entries.get(actionId)?.owner === owner)
            return;
        this.set(actionId, owner, conversation);
    }
    /** The person confirmed or cancelled the action in `conversation`: it belongs there from now on. */
    decided(actionId, owner, conversation) {
        this.set(actionId, owner, conversation);
    }
    /** The chat `actionId` belongs to for `owner`, or null when no chat is known to hold it. */
    conversationFor(actionId, owner) {
        const entry = this.entries.get(actionId);
        return entry && entry.owner === owner ? entry.conversation : null;
    }
    /** A deleted chat: its actions belong to no chat now. */
    forgetConversation(conversation) {
        for (const [actionId, entry] of [...this.entries])
            if (entry.conversation === conversation)
                this.entries.delete(actionId);
    }
    /** Another identity, or the widget's own reset. */
    clear() {
        this.entries.clear();
    }
    /** A chat with no id yet (a new chat before its first save) binds nothing: its own transcript still shows the card. */
    set(actionId, owner, conversation) {
        if (!actionId || !owner || !conversation)
            return;
        this.entries.delete(actionId);
        this.entries.set(actionId, { owner, conversation });
        if (this.entries.size > ACTION_CONVERSATIONS_MAX)
            this.entries.delete(this.entries.keys().next().value);
    }
}
/** Whether a transcript shows the action: one of its messages carries the action's card, or the outcome recorded for it. */
export function holdsAction(messages, actionId) {
    return messages.some((message) => message.actionOutcomeId === actionId || Boolean(message.actions?.some((action) => action.id === actionId)));
}
//# sourceMappingURL=actionConversations.js.map